import React, { useEffect, useState } from 'react';
import styles from './RecordPaymentModal.module.css';
import { uiMethodToBackend } from '../paymentsTypes';
import { useInvoicesStore } from '../../../../store/invoicesStore';
import { usePaymentsStore } from '../../../../store/paymentsStore';

interface RecordPaymentModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormState {
  name: string;
  amount: string;
  paymentMode: string;
  paymentDate: string;
  referenceNumber: string;
  collectedBy: string;
  notes: string;
}

const INITIAL_FORM: FormState = {
  name: '',
  amount: '',
  paymentMode: '',
  paymentDate: '',
  referenceNumber: '',
  collectedBy: '',
  notes: '',
};

const PAYMENT_MODES = ['Cash', 'Check', 'Bank Transfer', 'Credit Card'] as const;

export const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({ open, onClose }) => {
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const invoices = useInvoicesStore((s) => s.items);
  const fetchInvoices = useInvoicesStore((s) => s.fetchList);
  const recordPayment = usePaymentsStore((s) => s.record);
  const refreshPayments = usePaymentsStore((s) => s.fetchList);

  useEffect(() => {
    if (open && invoices.length === 0) fetchInvoices().catch(() => undefined);
  }, [open, invoices.length, fetchInvoices]);

  const outstanding = invoices.filter((i) => i.status === 'sent' || i.status === 'overdue');

  const handleChange = (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleClose = () => {
    setForm(INITIAL_FORM);
    setSubmitError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!form.referenceNumber || !form.amount || !form.paymentMode) {
      setSubmitError('Invoice, amount and payment mode are required');
      return;
    }
    const amount = parseFloat(form.amount.replace(/[^0-9.]/g, ''));
    if (isNaN(amount) || amount <= 0) {
      setSubmitError('Amount must be a positive number');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await recordPayment({
        invoice_id: Number(form.referenceNumber),
        amount,
        payment_method: uiMethodToBackend(form.paymentMode),
        payment_date: form.paymentDate || undefined,
        reference_number: form.name || undefined,
        notes: form.notes || undefined,
      });
      await refreshPayments();
      handleClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className={styles.modal}>
        {/* Modal Header */}
        <div className={styles.modalHeader}>
          <div>
            <h2 className={styles.modalTitle}>Record Payment</h2>
            <p className={styles.modalSubtitle}>
              Fill in the details below &bull; <span className={styles.required}>*</span> = Required
            </p>
          </div>
          <button className={styles.closeBtn} onClick={handleClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className={styles.modalBody}>
          {/* Section: Basic Information */}
          <div className={styles.sectionHeader}>
            <span className={styles.sectionIcon}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            </span>
            <h3 className={styles.sectionTitle}>1. Basic Information</h3>
          </div>

          {/* Two-column form grid */}
          <div className={styles.formGrid}>
            {/* Name */}
            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                Name <span className={styles.required}>*</span>
              </label>
              <input
                className={styles.input}
                type="text"
                value={form.name}
                onChange={handleChange('name')}
              />
            </div>

            {/* Amount */}
            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                Amount <span className={styles.required}>*</span>
              </label>
              <input
                className={styles.input}
                type="text"
                placeholder="$0.00"
                value={form.amount}
                onChange={handleChange('amount')}
              />
            </div>

            {/* Payment Mode */}
            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                Payment Mode <span className={styles.required}>*</span>
              </label>
              <input
                className={styles.input}
                type="text"
                placeholder="Cash"
                value={form.paymentMode}
                onChange={handleChange('paymentMode')}
              />
            </div>

            {/* Payment Date */}
            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                Payment Date <span className={styles.required}>*</span>
              </label>
              <input
                className={styles.input}
                type="date"
                value={form.paymentDate}
                onChange={handleChange('paymentDate')}
              />
            </div>

            {/* Invoice (used as reference target) */}
            <div className={styles.fieldGroup}>
              <label className={styles.label}>
                Invoice <span className={styles.required}>*</span>
              </label>
              <div className={styles.selectWrapper}>
                <select
                  className={styles.select}
                  value={form.referenceNumber}
                  onChange={handleChange('referenceNumber')}
                >
                  <option value="">Select an outstanding invoice</option>
                  {outstanding.map((inv) => (
                    <option key={inv.id} value={String(inv.id)}>
                      {inv.invoice_number} — ${Number(inv.total_amount).toFixed(2)}
                    </option>
                  ))}
                </select>
                <svg className={styles.chevron} viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7 10l5 5 5-5z" />
                </svg>
              </div>
            </div>

            {/* Collected By (free text — current user is recorded server-side via JWT) */}
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Collected By</label>
              <input
                className={styles.input}
                type="text"
                placeholder="Auto-tracked by current user"
                value={form.collectedBy}
                onChange={handleChange('collectedBy')}
              />
            </div>
          </div>

          {submitError && (
            <div style={{ margin: '12px 0', padding: 12, background: '#FEF2F2', color: '#B91C1C', borderRadius: 6 }}>
              {submitError}
            </div>
          )}

          {/* Notes */}
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Notes</label>
            <textarea
              className={styles.notesArea}
              value={form.notes}
              onChange={handleChange('notes')}
              rows={4}
            />
          </div>

          {/* Required hint */}
          <p className={styles.requiredHint}>
            <span className={styles.required}>*</span> = Required field
          </p>
        </div>

        {/* Modal Footer */}
        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={handleClose} disabled={submitting}>Cancel</button>
          <button className={styles.submitBtn} onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Recording…' : 'Record Payment'}
          </button>
        </div>
      </div>
    </div>
  );
};
